import CopyAllIcon from "@mui/icons-material/CopyAll";
import ReplayIcon from "@mui/icons-material/Replay";
import { Box, Button, Stack, Tooltip } from "@mui/material";
import * as React from "react";
import { useAsync, useKeyPress } from "react-use";
import { v4 as uuidv4 } from "uuid";
import { useApi } from "../../../../../containers/ApiContainer";
import { Ramp } from "../../../../../types";
import { MeasuringTool } from "../../../../../widgets/Layout/MeasuringTool";
import Canvas from "../../../../../widgets/useCanvas";
import { ZoomControl } from "../../../../../widgets/ZoomControl";
import { useLocalRampState, useRampEditorState } from "../RampEditorPresenter";

import { useActiveFBOs } from "../../../../../containers/ActiveFBOContainer";
import { DraggingScrollContainer } from "../../../../../widgets/DraggingScrollContainer/DraggingScrollContainer";
import { Loading } from "../../../../../widgets/Loading";
import { CanvasTextInput } from "./CanvasTextInput";
import { drawMain } from "./drawing";
import { useCanvasHotkeys } from "./EventHandlers/useCanvasHotkeys";
import { useOnCanvasClick } from "./EventHandlers/useOnCanvasClick";
import { useOnContextMenu } from "./EventHandlers/useOnContextMenu";
import { clickedShape, useOnMouseDown } from "./EventHandlers/useOnMouseDown";
import { useOnMouseMove } from "./EventHandlers/useOnMouseMove";
import { usePatterns } from "./patterns/usePatterns";
import { SHAPE_COLORS, ShapeEditDialog } from "./ShapeEditDialog";

type Props = {
  ramp: Ramp;
  setRamp: (ramp: Ramp) => void;
  readOnly?: boolean;
  selectedTool: string;
  setSelectedTool: (tool: string) => void;
};

export type ContextMenu = {
  x: number;
  y: number;
  shapeId: string;
};

export type DraggingPoint = {
  shapeId: string;
  pointIndex: number;
};
export type DraggingShape = {
  shapeId: string;
  offset: Point;
};

export type Point = { x: number; y: number };

export type ReferencePoint = {
  name: 'north' | 'south' | 'east' | 'west' | 'center';
  validOrientations: ('nose_to_line' | 'tail_to_line' | 'center')[];
};

// Aircraft size categories based on typical wingspan
export type SizeCategory =
  | "very-small"    // Max 40ft wingspan
  | "small"         // Max 60ft wingspan
  | "midsize"       // Max 80ft wingspan
  | "super-midsize" // Max 100ft wingspan
  | "large";        // Max 120ft wingspan

// Size category UI options - maxFeet = max allowable wingspan
export const SIZE_CATEGORY_OPTIONS = [
  { value: 'very-small' as SizeCategory, label: 'Very Small (≤40ft)', maxFeet: 40 },
  { value: 'small' as SizeCategory, label: 'Small (≤60ft)', maxFeet: 60 },
  { value: 'midsize' as SizeCategory, label: 'Midsize (≤80ft)', maxFeet: 80 },
  { value: 'super-midsize' as SizeCategory, label: 'S-Mid (≤100ft)', maxFeet: 100 },
  { value: 'large' as SizeCategory, label: 'Large (≤120ft)', maxFeet: 120 },
];

/**
 * Map spot size in feet to size category
 * Uses maxFeet from SIZE_CATEGORY_OPTIONS
 */
export function getDefaultSizeCategory(spotFeet: number): SizeCategory {
  for (const option of SIZE_CATEGORY_OPTIONS) {
    if (spotFeet <= option.maxFeet) {
      return option.value;
    }
  }
  return "large"; // Fallback for > 120ft
}

/**
 * Get max allowed sizes for a spot based on its own size
 * RULE: Spots can only allow aircraft sizes <= their own size
 */
export function getMaxAllowedSizes(spotSize: SizeCategory): SizeCategory[] {
  const sizeHierarchy: SizeCategory[] = ["very-small", "small", "midsize", "super-midsize", "large"];
  const maxIndex = sizeHierarchy.indexOf(spotSize);
  return sizeHierarchy.slice(0, maxIndex + 1);
}

/**
 * Zone metadata - stored in shape.metadata for Zone shapes
 *
 * Note: Zone name is stored in shape.text
 * Note: Zone priority is stored in shape.priority (0-10, higher = more preferred)
 */
export type ZoneMetadata = {
  /**
   * Disabled flag for zone
   * When true, all spots in this zone are unavailable to parking algorithm
   * Use for closing entire zones during maintenance/construction
   * Default: false (enabled)
   */
  disabled?: boolean;
};

/**
 * Egress option representing one possible exit path for a spot
 * Spots can have multiple egress lanes - aircraft chooses best available at departure
 */
export type EgressOption = {
  /** Shape ID of the egress lane */
  laneId: string;
  /** Display name (e.g., "Lane 2") */
  laneName: string;
  /** Spots blocked if this lane is used */
  blocksSpotIds: string[];
};

/**
 * Spot metadata - stored in shape.metadata for Spot shapes
 * Contains temporal parking algorithm configuration
 */
export type SpotMetadata = {
  /**
   * Aircraft size categories allowed to park in this spot
   * RULE: Can only include sizes <= spot's own size category
   * Default: [spot's own size] (e.g., 60ft spot defaults to ["small"])
   */
  allowedSizes?: SizeCategory[];

  /**
   * Egress lane shape IDs this spot can use for departure
   * References Line shapes tagged with "egress_lane"
   * Array because some spots have multiple exit options
   */
  egressLanes?: string[];

  /**
   * Per-lane egress options with blocking relationships
   * Auto-calculated from egressLanes - shows which spots are blocked by each egress option
   * Aircraft will choose the lane with fewest blocks at departure time
   */
  egressOptions?: EgressOption[];

  /**
   * Zone ID this spot belongs to
   * Auto-assigned based on which zone polygon contains the spot's center point
   */
  zoneId?: string;

  /**
   * Gap spot flag (Phase 2a)
   * Marks this spot as a gap spot - smaller spot that overlaps larger spots spatially
   * When true, spot becomes available only when surrounding larger spots are occupied
   */
  isGapSpot?: boolean;

  /**
   * Required occupied spots for gap spot (Phase 2c)
   * Spot IDs that must be occupied for this gap spot to be available
   * Auto-detected based on spatial overlap with larger spots
   * Only applies when isGapSpot is true
   */
  requiresOccupiedSpots?: string[];

  /**
   * Maximum dwell time in minutes for this spot
   * Used for overflow/emergency spots that should only hold aircraft temporarily
   * undefined = no limit (default for normal spots)
   * Examples: 60 = 1 hour, 180 = 3 hours, 1440 = 1 day
   */
  max_dwell_minutes?: number;

  /**
   * Minimum dwell time in minutes for this spot
   * Used for remote/overflow spots to reserve them for long-stay aircraft
   * Aircraft staying >= min_dwell_minutes get priority placement (bonus)
   * Aircraft staying < min_dwell_minutes are discouraged (penalty)
   * undefined = no minimum (default for normal spots)
   * Examples: 480 = 8 hours, 1440 = 1 day, 4320 = 3 days
   */
  min_dwell_minutes?: number;

  /**
   * Disabled flag for spot
   * When true, spot is unavailable to parking algorithm
   * Use for maintenance, construction, or temporarily closing individual spots
   * Default: false (enabled)
   */
  disabled?: boolean;
};

/** Position options for Line text labels */
export type LineTextPosition = 'start' | 'middle' | 'end';

/**
 * Line metadata - stored in shape.metadata for Line shapes
 * Contains text label positioning configuration
 */
export type LineMetadata = {
  /** Position of text label relative to line. Default: 'middle' */
  text_position?: LineTextPosition;
  /** Whether to show text label. Default: true */
  show_text?: boolean;
};

export type Shape = {
  id: string;
  fbo_id: string;
  ramp_id: string;
  hangar_id?: string;
  tags: string[];
  points: Point[];
  type:
    | "Line"
    | "Text"
    | "Area"
    | "Tie Down T"
    | "Double T"
    | "Circle"
    | "Helipad"
    | "Compass Rose"
    | "Spot"
    | "Zone";
  locked: boolean;
  stroke_style?: string;
  line_style?: "solid" | "dashed";
  line_width?: number;
  fill_style?: string;
  fill_pattern?: string;
  text?: string;
  font_size?: number;
  // this is the height of the image being edited in pixels. we use this to maintain
  // the relative font size
  reference_image_height?: number;

  /**
   * Priority for parking assignments (0-10, higher = preferred)
   * Used by both Spots and Zones
   */
  priority?: number;

  /**
   * Reference points for spots - STORED AS TOP-LEVEL COLUMN (not in metadata)
   * WHY: Complex type used by general auto-parking algorithm (not temporal-specific)
   * Contains orientation data and positioning info for runAutoParking()
   */
  reference_points?: ReferencePoint[];

  /**
   * Algorithm-specific metadata stored as JSONB
   * - For Zones: ZoneMetadata (currently empty, uses shape.text for name)
   * - For Spots: SpotMetadata (allowedSizes, egressLanes, blocksSpotIds, zoneId)
   * - For Lines: LineMetadata (text_position, show_text)
   */
  metadata?: ZoneMetadata | SpotMetadata | LineMetadata;
};

export const makeDefaultShape = (fbo_id: string, ramp_id: string): Shape => ({
  id: uuidv4(),
  ramp_id,
  fbo_id,
  tags: [],
  points: [],
  type: "Line",
  locked: false,
  line_style: "solid",
  line_width: 1,
  stroke_style: SHAPE_COLORS[0],
  fill_style: "",
  fill_pattern: "",
  font_size: 24,
  text: "",
  priority: 5,
});

export const RampCanvas: React.FC<Props> = ({
  selectedTool,
  setSelectedTool,
  readOnly = false,
}) => {
  const { activeFBO } = useActiveFBOs();
  const { ramp, setRamp, highlightedShapes, activeShapes, setActiveShapes } =
    useLocalRampState();

  const [history, setHistory] = React.useState<Shape[][]>([]); // History stack
  const { postgrest } = useApi();
  const {
    showReferenceImage,
    showMeasurements,
    showGuideLines,
    showCalibrationPoints,
    highlightedCalibrationPoint,
  } = useRampEditorState();
  const [zoomLevel, setZoomLevel] = React.useState<number>(1);
  const [canvasWidth, setCanvasWidth] = React.useState<number>(600);

  const div = React.useCallback((node) => {
    if (node?.offsetWidth) {
      setCanvasWidth(node.offsetWidth * 0.9);
    }
  }, []);

  const rampImage = useAsync(async () => {
    if (ramp.id && showReferenceImage) {
      try {
        const { data } = await postgrest
          .from("ramp")
          .select("reference_image")
          .eq("id", ramp.id)
          .single();
        return data.reference_image;
      } catch (error) {
        console.error(
          `Error loading reference image for ramp: ${ramp.id}`,
          error
        );
        return null;
      }
    }
    return null;
  }, [ramp.reference_image]);

  const canvasRef = React.createRef<HTMLCanvasElement>();
  const pixelsToFeetRatio = canvasWidth / (ramp?.width ?? 1);
  const canvasHeight = (canvasWidth * ramp?.depth) / ramp?.width;
  const pixelsToFeet = (px: number) => px / pixelsToFeetRatio;

  const { loading: loadingPatterns, patterns } = usePatterns();

  // convert shapes to pixels
  const shapes = React.useMemo(
    () =>
      ramp?.shapes?.map((shape) => ({
        ...shape,
        points: shape.points.map((point) => ({
          x: point.x * pixelsToFeetRatio,
          y: point.y * pixelsToFeetRatio,
        })),
      })) || [],
    [ramp?.shapes, pixelsToFeetRatio]
  );

  // returns active shapes in pixels
  const getActiveShapes = React.useCallback(() => {
    return shapes
      .filter(
        (s) => activeShapes.includes(s.id) || draggingShape?.shapeId === s.id
      )
      .filter((s) => !s.locked);
  }, [shapes, activeShapes]);

  // set a given shape, converting from pixels to feet
  const setShape = React.useCallback(
    (id: string, zShape: Shape) => {
      setRamp((currentRamp) => {
        if (!currentRamp) {
          console.error('[RampCanvas] setShape called but currentRamp is undefined');
          return currentRamp;
        }
        return {
          ...currentRamp,
          shapes: currentRamp.shapes.map((shape) => {
            if (shape.id === id) {
              // zShape is in pixels, convert to feet
              return {
                ...zShape,
                points: zShape.points.map((point) => ({
                  x: point.x / pixelsToFeetRatio,
                  y: point.y / pixelsToFeetRatio,
                })),
              };
            }
            // Other shapes are already in feet, return as-is
            return shape;
          }),
        };
      });
    },
    [setRamp, pixelsToFeetRatio]
  );

  // set shapes, converting from pixels to feet
  const setShapes = React.useCallback(
    (shapesOrUpdater: Shape[] | ((prevShapes: Shape[]) => Shape[])) => {
      setRamp((currentRamp) => {
        if (!currentRamp) {
          console.error('[RampCanvas] setShapes called but currentRamp is undefined');
          return currentRamp;
        }

        const updatedShapes = typeof shapesOrUpdater === 'function'
          ? shapesOrUpdater(currentRamp.shapes.map((shape) => ({
              ...shape,
              points: shape.points.map((point) => ({
                x: point.x * pixelsToFeetRatio,
                y: point.y * pixelsToFeetRatio,
              })),
            })))
          : shapesOrUpdater;

        return {
          ...currentRamp,
          shapes: updatedShapes.map((shape) => ({
            ...shape,
            points: shape.points.map((point) => ({
              x: point.x / pixelsToFeetRatio,
              y: point.y / pixelsToFeetRatio,
            })),
          })),
        };
      });
    },
    [setRamp, pixelsToFeetRatio]
  );

  const duplicateShapes = () => {
    if (activeShapes.length > 0) {
      const updatedShapes = [...shapes];
      getActiveShapes().forEach((shape) => {
        updatedShapes.unshift({
          ...shape,
          id: uuidv4(),
          points: shape.points.map((point) => ({
            x: point.x + pixelsToFeetRatio * 25,
            y: point.y + pixelsToFeetRatio * 25,
          })),
        });
      });
      setShapes(updatedShapes);
      setActiveShapes([...activeShapes, updatedShapes[0].id]);
    }
  };

  // Undo the last action
  const undo = () => {
    if (history.length > 0) {
      const previousState = history[history.length - 1]; // Get the last saved state
      if (!previousState) {
        return;
      }
      setShapes(previousState); // Revert to the last state
      setHistory((prevHistory) => prevHistory.slice(0, -1)); // Remove the last state from history
    }
  };

  const [currentShape, setCurrentShape] = React.useState<Shape>(
    makeDefaultShape(ramp.fbo_id, ramp.id)
  );
  const [draggingPoint, setDraggingPoint] =
    React.useState<DraggingPoint | null>(null);
  const [draggingShape, setDraggingShape] =
    React.useState<DraggingShape | null>(null);
  const [mousePosition, setMousePosition] = React.useState<Point | null>(null);
  const [contextMenu, setContextMenu] = React.useState<ContextMenu | null>(
    null
  );
  const [previewSpots, setPreviewSpots] = React.useState<Shape[]>([]);
  const [textPrompt, setTextPrompt] = React.useState<{
    open: boolean;
    x?: number;
    y?: number;
  }>({
    open: false,
  });
  const [cursor, setCursor] = React.useState<string | null>(null);

  const [isShiftPressed] = useKeyPress("Shift");
  const [isCommandPressed] = useKeyPress("Meta");
  const [isOptPressed] = useKeyPress("Alt");

  useCanvasHotkeys({
    activeShapes,
    setActiveShapes,
    getActiveShapes,
    pixelsToFeetRatio,
    shapes,
    setShapes,
    undo,
    duplicateShapes,
  });

  const draw = React.useCallback(
    (context: CanvasRenderingContext2D) => {
      drawMain(context, {
        shapes,
        currentShape,
        highlightedShapes,
        mousePosition,
        calibrationPoints: ramp?.calibration_points,
        showCalibrationPoints,
        highlightedCalibrationPoint,
        canvasRef,
        pixelsToFeet,
        showMeasurements,
        showGuideLines,
        pixelsToFeetRatio,
        activeShapes,
        patterns,
        isShiftPressed,
        zoomLevel,
        isMetric: activeFBO?.settings?.useMetricSystem || false,
        previewSpots,
      });
    },
    [
      shapes,
      currentShape,
      previewSpots,
      activeShapes,
      mousePosition,
      isShiftPressed,
      highlightedShapes,
      canvasRef,
      pixelsToFeet,
      showMeasurements,
      showGuideLines,
      showCalibrationPoints,
      ramp?.calibration_points,
      highlightedCalibrationPoint,
      pixelsToFeetRatio,
      patterns,
    ]
  );

  React.useEffect(() => {
    const canvas = canvasRef.current;
    const context = canvas?.getContext("2d");
    if (canvas && context) {
      draw(context);
    }
  }, [canvasRef, draw]);

  // Simple check to prevent "undefined" in image URLs
  const safeImageUrl = React.useMemo(() => {
    if (
      rampImage.value &&
      typeof rampImage.value === "string" &&
      rampImage.value.includes("undefined")
    ) {
      console.warn(`Invalid URL with 'undefined' detected: ${rampImage.value}`);
      return null;
    }
    return rampImage.value;
  }, [rampImage.value]);

  // Add a point to the current shape or complete the shape if clicking on the first point
  const handleCanvasClick = useOnCanvasClick({
    selectedTool,
    shapes,
    setShapes,
    currentShape,
    setCurrentShape,
    setMousePosition,
    setSelectedTool,
    ramp,
    activeShapes,
    setActiveShapes,
    canvasRef,
    isCommandPressed,
    isOptPressed,
    isShiftPressed,
    setShape,
    setContextMenu,
    pixelsToFeetRatio,
  });

  // Start dragging a point (either from existing shapes or the current shape)
  const handleMouseDown = useOnMouseDown({
    selectedTool,
    setSelectedTool,
    shapes,
    setShapes,
    currentShape,
    setDraggingPoint,
    setDraggingShape,
    draggingPoint,
    draggingShape,
    canvasRef,
    ramp,
    setHistory,
    setTextPrompt,
  });

  // Drag the point
  const handleMouseMove = useOnMouseMove({
    selectedTool,
    shapes,
    setShapes,
    currentShape,
    setDraggingPoint,
    setDraggingShape,
    draggingPoint,
    draggingShape,
    canvasRef,
    ramp,
    isShiftPressed,
    setMousePosition,
    getActiveShapes,
    setSelectedTool,
  });

  const handleContextMenu = useOnContextMenu({
    selectedTool,
    shapes,
    setContextMenu,
    canvasRef,
  });

  // Finish dragging
  const handleMouseUp = (event: React.MouseEvent) => {
    setDraggingShape(null);
    setDraggingPoint(null);
  };

  if (loadingPatterns) {
    return <Loading />;
  }

  return (
    <Stack direction="column" spacing={1}>
      <Stack direction="row" justifyContent="flex-end" spacing={2}>
        <Tooltip
          title={navigator.platform.includes("Mac") ? `⌘ + d` : `alt + d`}
        >
          <Button
            variant="contained"
            color="info"
            startIcon={<CopyAllIcon />}
            onClick={duplicateShapes}
          >
            Copy Selected
          </Button>
        </Tooltip>
        <Tooltip
          title={navigator.platform.includes("Mac") ? `⌘ + z` : `alt + z`}
        >
          <Button
            variant="contained"
            color="info"
            startIcon={<ReplayIcon />}
            onClick={undo}
          >
            Undo
          </Button>
        </Tooltip>
      </Stack>
      <Box
        ref={div}
        sx={{
          cursor,
          position: "relative",
          overflowX: "scroll",
        }}
      >
        <DraggingScrollContainer
          zoomLevel={zoomLevel}
          feetToPixels={1 / pixelsToFeetRatio}
          disabled={false}
          onClick={handleCanvasClick}
          onMouseDown={handleMouseDown}
          onMouseMove={handleMouseMove}
          onMouseUp={handleMouseUp}
          onContextMenu={handleContextMenu}
          preventDefault={(event: React.MouseEvent) => {
            if (selectedTool === "Select") {
              return clickedShape(canvasRef, event, shapes);
            }
            return true;
          }}
        >
          <MeasuringTool
            pixelsToFeetRatio={pixelsToFeetRatio}
            overlayExpansionRatio={1}
          >
            <Canvas
              ref={canvasRef as React.RefObject<HTMLCanvasElement>}
              draw={draw}
              width={canvasWidth}
              height={canvasHeight}
              onClick={handleCanvasClick}
              onMouseDown={handleMouseDown}
              onMouseMove={(evt) => {
                if (clickedShape(canvasRef, evt, shapes)) {
                  setCursor("default");
                } else {
                  setCursor(null);
                }
                handleMouseMove(evt);
              }}
              onMouseUp={handleMouseUp}
              onContextMenu={handleContextMenu} // Attach the right-click handler
              style={{
                border: "1px solid black",
                cursor: selectedTool === "Select" ? cursor : "crosshair",
                backgroundImage:
                  showReferenceImage && safeImageUrl
                    ? `url(${safeImageUrl})`
                    : undefined,
                backgroundSize: showReferenceImage && "cover",
                margin: "20px",
              }}
            />
          </MeasuringTool>
        </DraggingScrollContainer>
      </Box>

      <ShapeEditDialog
        contextMenu={contextMenu}
        setContextMenu={setContextMenu}
        onClose={() => {
          setDraggingShape(null);
          setDraggingPoint(null);
          setContextMenu(null);
          setPreviewSpots([]);
        }}
        shapes={shapes}
        setShapes={setShapes}
        canvasHeight={canvasHeight}
        canvasWidth={canvasWidth}
        previewSpots={previewSpots}
        setPreviewSpots={setPreviewSpots}
      />
      <ZoomControl
        currentZoom={canvasWidth}
        increment={0.1}
        onClickPlus={() => {
          // Limit zoom to 250%
          if (zoomLevel < 2.5) {
            setZoomLevel(zoomLevel * 1.1);
            setCanvasWidth(canvasWidth * 1.1);
            setCurrentShape({
              ...currentShape,
              points: currentShape.points.map((point) => ({
                x: point.x * 1.1,
                y: point.y * 1.1,
              })),
            });
          }
        }}
        onClickMinus={() => {
          setZoomLevel(zoomLevel * 0.9);
          setCanvasWidth(canvasWidth * 0.9);
          setCurrentShape({
            ...currentShape,
            points: currentShape.points.map((point) => ({
              x: point.x * 0.9,
              y: point.y * 0.9,
            })),
          });
        }}
      />
      <CanvasTextInput
        open={textPrompt.open}
        setOpen={(open) => setTextPrompt({ open })}
        onDone={(text) => {
          const updatedShapes = [...shapes];
          updatedShapes.unshift({
            id: uuidv4(),
            fbo_id: ramp.fbo_id,
            ramp_id: ramp.id,
            tags: [],
            points: [{ x: textPrompt.x, y: textPrompt.y }],
            locked: false,
            type: "Text",
            stroke_style: "white",
            line_style: "solid",
            line_width: 3,
            text,
            font_size: 18,
            reference_image_height: canvasHeight,
          });
          setShapes(updatedShapes);
          setSelectedTool("Select");
        }}
      />
    </Stack>
  );
};
